import { useTranslation } from "react-i18next";
import React, { useState, Children } from 'react';
import { ArrowLeftIcon, PlusIcon, TrashIcon, AlertCircleIcon } from 'lucide-react';
interface AddRoomFormProps {
  onSave: (roomData: any) => void;
  onCancel: () => void;
}
export function AddRoomForm({
  onSave,
  onCancel
}: AddRoomFormProps) {
  const {
    t
  } = useTranslation("rooms");
  const [roomData, setRoomData] = useState({
    name: '',
    category: '',
    code: '',
    description: '',
    isSmoking: false,
    isAccessible: false,
    isActive: false,
    view: '',
    size: '',
    amenities: [] as string[],
    baseAdultsOccupancy: 2,
    baseChildrenOccupancy: 0,
    maxOccupancy: 2,
    allowAdditionalAdults: false,
    allowAdditionalChildren: false,
    bedConfigurations: [{
      type: '',
      count: 1
    }]
  });
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const amenityOptions = ['Wi-Fi', 'TV', 'Minibar', 'Air Conditioning', 'Balcony', 'Terrace', 'Ocean View', 'Private Pool', 'Jacuzzi', 'Slippers', 'Desk', 'Dining Area', 'Interconnecting Rooms', 'Non-Smoking', 'Beach Access', 'Breakfast Included'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const addBedConfig = () => {
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: [...prev.bedConfigurations, {
        type: '',
        count: 1
      }]
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-9787" data-mp="1wqtg0hfkvbpl">
      <div className="flex items-center" data-id="element-9788" data-mp="1rtjsx8fnvc4x">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-9789" data-mp="1rsrw5uabv8d2">
          
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-9790" data-mp="25ssyle68qnun" />
          <span data-id="element-9791" data-mp="21s0pqm1a6kyw">{t("components.AddRoomForm.add_room")}</span>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9792" data-mp="1e26eg8wm14gr">
        
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9793" data-mp="1944wjrax7b6v">
          <div data-id="element-9794" data-mp="1mv05t4pbzuaz">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9795" data-mp="1muqhdljxpv7e">
              
              {t("components.AddRoomForm.room_name")}<span className="text-red-500" data-id="element-9796" data-mp="1i1niqz0avvkc">*</span>
            </label>
            <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9797" data-mp="ucurvgyjcdwl" />
            
          </div>
          <div data-id="element-9798" data-mp="133ea99posi1a">
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9799" data-mp="133mksuyc7b9b">
              
              {t("components.AddRoomForm.room_category")}<span className="text-red-500" data-id="element-9800" data-mp="1czpmy0t6v9w9">*</span>
            </label>
            <div className="relative" data-id="element-9801" data-mp="1hqpl19e1ny0c">
              <select id="category" name="category" value={roomData.category} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-9802" data-mp="1hrr5u5kv3yv9">
                
                <option value="" data-id="element-9803" data-mp="1qpt3qc9aubeb">{t("components.AddRoomForm.select_room_category")}</option>
                {roomCategories.map(category => <option key={category} value={category} data-id="element-9804" data-mp="1voe3ybojwv2r">
                    {category.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-9805" data-mp="1dhj1fw9qi6j">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-9806" data-mp="6cluyeakuu0k">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-9807" data-mp="3uy7a4m1bpfxb" />
                  
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-9808" data-mp="3g8rotlkka2p0">
          <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9809" data-mp="3g9ue5p28jx2t">
            
            {t("components.AddRoomForm.room_code")}
          </label>
          <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_code")} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9810" data-mp="cr9ixjocn5r8" />
          
        </div>
        <div className="mb-6" data-id="element-9811" data-mp="2td9qxez2kvr">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9812" data-mp="2sixd57bzak6c">
            
            {t("components.AddRoomForm.description")}
          </label>
          <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} placeholder={t("components.AddRoomForm.attrs.enter_room_description")} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9813" data-mp="2nm6xum7zzslz" />
          
        </div>
        <div className="flex flex-wrap gap-6 mb-6" data-id="element-9814" data-mp="15lpjyoujtht6">
          <div className="flex items-center" data-id="element-9815" data-mp="1aksmo2n23bxz">
            <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9816" data-mp="1ajh7l6o5uhrk" />
            
            <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-9817" data-mp="10ofi29pz3dog">
              {t("components.AddRoomForm.smoking")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-9818" data-mp="ru13689cu55v">
            <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9819" data-mp="rsq4gmurnnlm" />
            
            <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-9820" data-mp="hyhf28118aro">
              
              {t("components.AddRoomForm.accessibility")}
            </label>
          </div>
          <div className="flex items-center" data-id="element-9821" data-mp="3qgqbjugiuz6r">
            <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9822" data-mp="89ao3vgvyasr" />
            
            <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-9823" data-mp="h8xfrpjtc28m">
              {t("components.AddRoomForm.status")}
            </label>
          </div>
        </div>
        {/* Room View and Size */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-9824" data-mp="qyh2x7ek6i9a">
          <div data-id="element-9825" data-mp="lzxcousxmkb4">
            <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9826" data-mp="m05tgxs6e9rl">
              
              {t("components.AddRoomForm.room_view")}<span className="text-red-500" data-id="element-9827" data-mp="10vlrfdvvmpt9">*</span>
            </label>
            <div className="relative" data-id="element-9828" data-mp="vzwor1ok8ssq">
              <select id="view" name="view" value={roomData.view} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-9829" data-mp="3crotifqgykxg">
                
                <option value="" data-id="element-9830" data-mp="3mputgrsptgzo">{t("components.AddRoomForm.select_room_view")}</option>
                {roomViews.map(view => <option key={view} value={view} data-id="element-9831" data-mp="3ronv9myn3jk0">
                    {view.replace('_', ' ')}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-9832" data-mp="2u2qkkbqi4iim">
                <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-9833" data-mp="2z0s1aq0gg5qx">
                  
                  <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-9834" data-mp="33y22f2x7i5ul" />
                  
                </svg>
              </div>
            </div>
          </div>
          <div data-id="element-9835" data-mp="2g9ipub74j6wp">
            <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9836" data-mp="2g8z92xt7mok9">
              
              {t("components.AddRoomForm.room_size_sq_m")}
            </label>
            <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9837" data-mp="tcjbvr9ahlxw" />
            
          </div>
        </div>
        {/* Room Amenities */}
        <div className="mb-6" data-id="element-9838" data-mp="jen150k9kt0b">
          <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9839" data-mp="efs712guji0w">
            
            {t("components.AddRoomForm.room_amenities")}
          </label>
          <select id="amenities" name="amenities" multiple value={roomData.amenities} onChange={e => {
          const options = Array.from(e.target.selectedOptions, option => option.value);
          setRoomData(prev => ({
            ...prev,
            amenities: options
          }));
        }} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9840" data-mp="9mz4ml8uolrz">
            
            {amenityOptions.map(amenity => <option key={amenity} value={amenity} data-id="element-9841" data-mp="3mxy4ps942zty">
                {amenity}
              </option>)}
          </select>
        </div>
        {/* Occupancy Settings */}
        <div className="mb-6" data-id="element-9842" data-mp="m92xiakwqc78">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-9843" data-mp="m7rsy8jn10f2">
            {t("components.AddRoomForm.occupancy_settings")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-9844" data-mp="vz5roe3rlw75">
            <div data-id="element-9845" data-mp="89jqxm7qv8x1">
              <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9846" data-mp="891lq033tkcz">
                
                {t("components.AddRoomForm.base_adults_occupancy")}<span className="text-red-500" data-id="element-9847" data-mp="ha9j8fxsqh60">*</span>
              </label>
              <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9848" data-mp="h8ex4yasgj0c" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-9849" data-mp="1npjwzlxpq077">
                {t("components.AddRoomForm.standard_number_of_allowed_adults_for")}
              </p>
            </div>
            <div data-id="element-9850" data-mp="208xlp1ve4nkw">
              <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9851" data-mp="209fqwo016c4y">
                
                {t("components.AddRoomForm.base_children_occupancy")}<span className="text-red-500" data-id="element-9852" data-mp="1r7zi2kov6i2u">*</span>
              </label>
              <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9853" data-mp="1rac71ht1hspe" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-9854" data-mp="257h976pmu7fd">
                {t("components.AddRoomForm.standard_number_of_allowed_children_for")}
              </p>
            </div>
            <div data-id="element-9855" data-mp="2xwo6y3h9iszk">
              <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9856" data-mp="2xw66eunkhslu">
                
                {t("components.AddRoomForm.maximum_occupancy")}<span className="text-red-500" data-id="element-9857" data-mp="3ck6fxe1uy9rs">*</span>
              </label>
              <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9858" data-mp="37kk3v7xrmzkk" />
              
              <p className="text-xs text-gray-500 mt-1" data-id="element-9859" data-mp="2ykn86mcyl0g0">
                {t("components.AddRoomForm.maximum_number_of_guests_allowed")}
              </p>
            </div>
          </div>
          <div className="space-y-2 mb-4" data-id="element-9860" data-mp="2otka7tfbt1ky">
            <div className="flex items-center" data-id="element-9861" data-mp="2oshmfudavf99">
              <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9862" data-mp="2jupr0qdg5b0b" />
              
              <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-9863" data-mp="2asztd2sz1fu3">
                
                {t("components.AddRoomForm.allow_additional_adults")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-9864" data-mp="lcvzc3v8a7ls">
              {t("components.AddRoomForm.allow_additional_adults_not_exceeding_total")}
            </p>
            <div className="flex items-center mt-2" data-id="element-9865" data-mp="udls0yp6zhva">
              <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-9866" data-mp="uc125mdton3r" />
              
              <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-9867" data-mp="6pb1oph4h0l9">
                
                {t("components.AddRoomForm.allow_additional_children")}
              </label>
            </div>
            <p className="text-xs text-gray-500 ml-6" data-id="element-9868" data-mp="gfnhy5hwm7ll">
              {t("components.AddRoomForm.allow_additional_children_not_exceeding_total")}
            </p>
          </div>
          {/* Occupancy Summary */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-9869" data-mp="3k1cortbl4wxv">
            <div className="flex" data-id="element-9870" data-mp="1ql8gb9qqtdii">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-9871" data-mp="1qi2p9oyk01k5" />
              <div data-id="element-9872" data-mp="1lmx4itgw0sxn">
                <p className="text-sm text-red-700" data-id="element-9873" data-mp="1lj9brwj9oajz">
                  {t("components.AddRoomForm.this_room_has_a_base_occupancy")}{' '}
                  {roomData.baseAdultsOccupancy} {t("components.AddRoomForm.adults_and")}{' '}
                  {roomData.baseChildrenOccupancy} {t("components.AddRoomForm.children_with_a_total_maximum_occupancy")} {roomData.maxOccupancy}.
                </p>
                <p className="text-sm text-red-700" data-id="element-9874" data-mp="24dxzz7isu6cg">
                  {t("components.AddRoomForm.additional_adults_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-9875" data-mp="2eb2qfjjognz2">
                  {t("components.AddRoomForm.additional_children_are_not_allowed")}
                </p>
                <p className="text-sm text-red-700" data-id="element-9876" data-mp="nzeulfbhpks2">
                  {t("components.AddRoomForm.additional_cost_per_child_or_adults")}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Bed Configuration */}
        <div className="mb-6" data-id="element-9877" data-mp="dsdf83l6pzto">
          <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-9878" data-mp="dvreh2yjxsww">
            {t("components.AddRoomForm.bed_configuration")}<span className="text-red-500" data-id="element-9879" data-mp="51d51h0pdclp">*</span>
          </h3>
          {roomData.bedConfigurations.map((config, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-9880" data-mp="3m9qecfon9yfo" data-mpi={index}>
            
              <div data-id="element-9881" data-mp="3r9300x3s4zgo">
                <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9882" data-mp="3r9cp8ih032lb">
                
                  {t("components.AddRoomForm.bed_type")}
                </label>
                <div className="relative" data-id="element-9883" data-mp="3i8euyrwd69fx">
                  <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-9884" data-mp="1bhfd7uihcnbl">
                  
                    <option value="" data-id="element-9885" data-mp="1e2wjdxkw5dh5">{t("components.AddRoomForm.select_bed_type")}</option>
                    {bedTypes.map(type => <option key={type} value={type} data-id="element-9886" data-mp="19zgn52cqhf4z">
                        {type}
                      </option>)}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-9887" data-mp="1022aldtxlahy">
                    <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-9888" data-mp="1xpkthhhb3szo">
                    
                      <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-9889" data-mp="1sq7xo78gjbky" />
                    
                    </svg>
                  </div>
                </div>
              </div>
              <div className="flex items-center" data-id="element-9890" data-mp="1iz3s1y4o932s">
                <div className="flex-grow" data-id="element-9891" data-mp="2ghdkx7gq965a">
                  <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9892" data-mp="2gi57jsscjtjf">
                  
                    {t("components.AddRoomForm.bed_count")}
                  </label>
                  <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-9893" data-mp="2nzl8c16oixxu" />
                
                </div>
                {index > 0 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-9894" data-mp="2xvy1mxybb9s2">
                
                    <TrashIcon size={18} data-id="element-9895" data-mp="36xnrw0ggk2o9" />
                  </button>}
              </div>
            </div>)}
          <button type="button" onClick={addBedConfig} className="flex items-center text-blue-600 hover:text-blue-800" data-id="element-9896" data-mp="3bqgx0pfu9d0v">
            
            <PlusIcon size={16} className="mr-1" data-id="element-9897" data-mp="3gqldifgqy7r3" />
            {t("components.AddRoomForm.add_bed_configuration")}
          </button>
        </div>
        {/* Submit Button */}
        <div className="flex justify-end" data-id="element-9898" data-mp="3pq00v61ma27d">
          <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-9899" data-mp="eur9ytjjlc9v">
            
            {t("components.AddRoomForm.save")}
          </button>
        </div>
      </form>
    </div>;
}